import type { ChatConversation, ChatHistory, ChatMessageView, ChatReportReason } from '@tg/shared';
import { CHAT_PAGE_SIZE } from '@tg/shared';
import { api, type RequestOptions } from '@/api/client';

/** the send answer: the stored message, bare or wrapped (`{ message }`) — both are accepted */
type SendAnswer = ChatMessageView | { message: ChatMessageView };

const unwrap = (r: SendAnswer): ChatMessageView =>
  'message' in r && typeof r.message === 'object' ? r.message : (r as ChatMessageView);

/** REST endpoints of the chat between friends (contracts in @tg/shared/chat, spec 2026-09-30 §2). */
export const chatApi = {
  list: async (o?: RequestOptions) =>
    (await api.get<{ conversations: ChatConversation[] }>('/api/chat', o)).conversations,
  history: (publicId: number, before?: string, o?: RequestOptions) =>
    api.get<ChatHistory>(
      `/api/chat/${publicId}?limit=${CHAT_PAGE_SIZE}${before ? `&before=${encodeURIComponent(before)}` : ''}`,
      o,
    ),
  send: async (publicId: number, text: string, clientId: string) =>
    unwrap(await api.post<SendAnswer>(`/api/chat/${publicId}`, { text, clientId })),
  read: (publicId: number, upTo: string) => api.post<unknown>(`/api/chat/${publicId}/read`, { upTo }),
  report: (messageId: string, reason: ChatReportReason, note?: string) =>
    api.post<unknown>('/api/chat/report', note ? { messageId, reason, note } : { messageId, reason }),
  block: (publicId: number) => api.post<unknown>('/api/friends/block', { publicId }),
  unblock: (publicId: number) => api.post<unknown>('/api/friends/unblock', { publicId }),
  /** players I blocked */
  blocked: async (o?: RequestOptions) =>
    (await api.get<{ items: Array<{ publicId: number; nickname: string; avatar: string }> }>('/api/friends/blocked', o))
      .items,
};
